import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Upload,
  message,
  Alert,
  List,
  Typography,
  Steps,
  Progress,
  Spin,
  Select,
  Table,
  Space,
  Tag,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import {
  downloadLineItemTemplate,
  parseLineItemFile,
  validateAllRows,
  transformToLineItems,
  LINE_ITEM_FIELDS,
} from '../utils/lineItemImport.utils';
import { useAIColumnMatching } from '../hooks/useAIColumnMatching';

const { Text, Title, Paragraph } = Typography;
const { Step } = Steps;

// Limits for file validation
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_ROWS = 100;

/**
 * LineItemImportModal Component
 *
 * Multi-step modal for importing line items from CSV/Excel files.
 * Features AI-powered column mapping with confidence scores.
 *
 * Step 0: Upload file + select sheet (if multiple)
 * Step 1: AI analyzes columns, shows mapping with confidence scores
 * Step 2: Preview table with validation errors/warnings
 * Step 3: Import complete
 */
const LineItemImportModal = ({
  visible,
  onClose,
  onImport,
  campaignIndex,
  campaignDefaults = {},
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState({
    rows: [],
    headers: [],
    columnMappings: {},
    sheetNames: [],
    selectedSheet: null,
  });
  const [columnMappings, setColumnMappings] = useState({});
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [copyFlightSchedules, setCopyFlightSchedules] = useState(true);

  // Track mounted state to prevent memory leaks from setState on unmounted component
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { getFieldOptions, analyzeColumnsWithAI, isAnalyzing, isAIAvailable } = useAIColumnMatching();
  const fieldOptions = useMemo(() => getFieldOptions(), [getFieldOptions]);

  const handleDownloadTemplate = () => {
    downloadLineItemTemplate();
    message.success('Template downloaded successfully');
  };

  /**
   * Process file after upload or sheet change
   */
  const processFile = async (uploadedFile, sheetName = null) => {
    try {
      const { rows, headers, columnMappings: autoMappings, sheetNames, selectedSheet } =
        await parseLineItemFile(uploadedFile, sheetName);

      // Check if component is still mounted
      if (!isMountedRef.current) return null;

      if (rows.length === 0) {
        message.error('Sheet is empty or contains no valid data rows');
        return null;
      }

      // Validate row count
      if (rows.length > MAX_ROWS) {
        message.error(`Too many rows. Maximum is ${MAX_ROWS} line items per import. Found ${rows.length} rows.`);
        return null;
      }

      return { rows, headers, autoMappings, sheetNames, selectedSheet };
    } catch (error) {
      if (isMountedRef.current) {
        message.error(error.message || 'Failed to parse file');
      }
      return null;
    }
  };

  /**
   * Run AI analysis on columns
   */
  const runAIAnalysis = async (headers, rows, fallbackMappings) => {
    if (!isAIAvailable()) {
      return fallbackMappings;
    }

    try {
      const aiMappings = await analyzeColumnsWithAI(headers, rows);
      if (aiMappings) {
        // Merge AI mappings with fallback (AI takes priority)
        const merged = { ...fallbackMappings };
        Object.keys(aiMappings).forEach(header => {
          if (aiMappings[header].field !== undefined) {
            merged[header] = {
              ...merged[header],
              ...aiMappings[header],
            };
          }
        });
        return merged;
      }
    } catch (error) {
      console.error('AI analysis failed, using rule-based matching:', error);
    }
    return fallbackMappings;
  };

  const handleFileUpload = async (uploadedFile) => {
    if (!uploadedFile.name.endsWith('.csv') && !uploadedFile.name.endsWith('.xlsx') && !uploadedFile.name.endsWith('.xls')) {
      message.error('Please upload a CSV or Excel file');
      return false;
    }

    // Validate file size
    if (uploadedFile.size > MAX_FILE_SIZE) {
      message.error(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return false;
    }

    setFile(uploadedFile);

    // Parse the file first
    message.loading('Parsing file...', 0);
    const result = await processFile(uploadedFile);
    message.destroy();

    if (!result) {
      setFile(null);
      return false;
    }

    const { rows, headers, autoMappings, sheetNames, selectedSheet } = result;

    // If multiple sheets, show sheet selector and wait
    if (sheetNames.length > 1) {
      setParsedData({
        rows,
        headers,
        columnMappings: autoMappings,
        sheetNames,
        selectedSheet,
      });
      setColumnMappings(autoMappings);
      message.info(`Found ${sheetNames.length} sheets. Select a sheet to continue.`);
      return false;
    }

    // Single sheet or CSV - proceed with AI analysis
    await proceedWithAIAnalysis(uploadedFile, rows, headers, autoMappings, sheetNames, selectedSheet);
    return false;
  };

  const proceedWithAIAnalysis = async (uploadedFile, rows, headers, autoMappings, sheetNames, selectedSheet) => {
    // Run AI analysis
    const finalMappings = await runAIAnalysis(headers, rows, autoMappings);

    if (!isMountedRef.current) return;

    setParsedData({
      rows,
      headers,
      columnMappings: finalMappings,
      sheetNames,
      selectedSheet,
    });
    setColumnMappings(finalMappings);

    // Count mapping stats
    const stats = getMappingStats(finalMappings);
    message.success(`Parsed ${rows.length} row(s). ${stats.autoMapped} auto-mapped, ${stats.reviewSuggested} need review.`);

    setCurrentStep(1);
  };

  const handleSheetChange = async (newSheetName) => {
    if (!file) return;

    message.loading('Loading sheet...', 0);
    const result = await processFile(file, newSheetName);
    message.destroy();

    if (!result) return;

    const { rows, headers, autoMappings, sheetNames, selectedSheet } = result;
    await proceedWithAIAnalysis(file, rows, headers, autoMappings, sheetNames, selectedSheet);
  };

  const handleColumnMappingChange = (header, fieldKey) => {
    setColumnMappings(prev => ({
      ...prev,
      [header]: {
        ...prev[header],
        field: fieldKey,
        confidence: fieldKey ? 1.0 : 0, // Manual selection = 100% confidence
        aiSuggested: false,
      },
    }));
  };

  const handleProceedToPreview = () => {
    // Check for duplicate column mappings
    const mappedFields = Object.values(columnMappings)
      .map(m => m?.field)
      .filter(Boolean);
    const duplicates = mappedFields.filter((field, index) => mappedFields.indexOf(field) !== index);

    if (duplicates.length > 0) {
      const duplicateLabels = [...new Set(duplicates)]
        .map(field => LINE_ITEM_FIELDS[field]?.label || field)
        .join(', ');
      message.error(`Duplicate column mappings detected: ${duplicateLabels}. Each field can only be mapped once.`);
      return;
    }

    const result = validateAllRows(parsedData.rows, columnMappings);
    setValidationResult(result);
    setCurrentStep(2);
  };

  const handleImport = async () => {
    if (!validationResult?.isValid) {
      message.error('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    setImportProgress({ current: 0, total: parsedData.rows.length });
    setCurrentStep(3);

    try {
      const lineItems = transformToLineItems(
        parsedData.rows,
        columnMappings,
        campaignDefaults
      );

      // Progress simulation with mounted check
      for (let i = 0; i < lineItems.length; i++) {
        if (!isMountedRef.current) return;
        setImportProgress({ current: i + 1, total: lineItems.length });
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      if (!isMountedRef.current) return;

      // Pass options including flight schedule copying preference
      const importOptions = {
        copyFlightSchedules,
        campaignFlightSchedules: campaignDefaults.campaignFlightSchedules,
      };
      onImport(lineItems, campaignIndex, importOptions);
      message.success(`Successfully imported ${lineItems.length} line item(s)`);

      setTimeout(() => {
        if (isMountedRef.current) {
          handleClose();
        }
      }, 500);
    } catch (error) {
      if (isMountedRef.current) {
        message.error(`Import failed: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setImporting(false);
      }
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFile(null);
    setParsedData({ rows: [], headers: [], columnMappings: {}, sheetNames: [], selectedSheet: null });
    setColumnMappings({});
    setValidationResult(null);
    setImportProgress({ current: 0, total: 0 });
    setCopyFlightSchedules(true);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  /**
   * Get mapping statistics for summary display
   */
  const getMappingStats = (mappings) => {
    let autoMapped = 0;
    let reviewSuggested = 0;
    let unmapped = 0;

    Object.values(mappings).forEach(m => {
      if (!m?.field) {
        unmapped++;
      } else if (m.confidence >= 0.9) {
        autoMapped++;
      } else if (m.confidence >= 0.5) {
        reviewSuggested++;
      } else {
        unmapped++;
      }
    });

    return { autoMapped, reviewSuggested, unmapped };
  };

  const mappingStats = useMemo(() => getMappingStats(columnMappings), [columnMappings]);

  /**
   * Get confidence badge for a mapping
   */
  const getConfidenceBadge = (mapping) => {
    const confidence = mapping?.confidence;
    const field = mapping?.field;

    if (!field) {
      return <Tag style={{ background: '#f5f5f5', color: '#8c8c8c', border: 'none' }}>Skip</Tag>;
    }
    if (confidence >= 0.9) {
      return (
        <Tag style={{ background: '#f6ffed', color: '#52c41a', border: 'none', fontWeight: 500 }}>
          ✓ {Math.round(confidence * 100)}%
        </Tag>
      );
    }
    if (confidence >= 0.7) {
      return (
        <Tag style={{ background: '#fffbe6', color: '#d48806', border: 'none', fontWeight: 500 }}>
          ⚠ {Math.round(confidence * 100)}%
        </Tag>
      );
    }
    if (confidence >= 0.5) {
      return (
        <Tag style={{ background: '#fff', color: '#8c8c8c', border: '1px solid #d9d9d9' }}>
          {Math.round(confidence * 100)}%
        </Tag>
      );
    }
    return <Tag style={{ background: '#f5f5f5', color: '#8c8c8c', border: 'none' }}>Manual</Tag>;
  };

  /**
   * Get row background color based on confidence
   */
  const getRowStyle = (mapping) => {
    const confidence = mapping?.confidence;
    const field = mapping?.field;

    if (!field) return {};
    if (confidence >= 0.9) return {};
    if (confidence >= 0.7) return { background: '#fffbe6' };
    return {};
  };

  /**
   * Get select border color based on confidence
   */
  const getSelectStyle = (mapping) => {
    const confidence = mapping?.confidence;
    const field = mapping?.field;

    const baseStyle = { width: '100%' };

    if (!field) return baseStyle;
    if (confidence >= 0.9) return { ...baseStyle, borderColor: '#52c41a' };
    if (confidence >= 0.7) return { ...baseStyle, borderColor: '#faad14' };
    return baseStyle;
  };

  const renderStepContent = () => {
    // Show AI analyzing state
    if (isAnalyzing) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <Title level={4} style={{ marginTop: 24 }}>
            ✨ Analyzing your columns with AI
          </Title>
          <Paragraph type="secondary">
            This may take a few seconds...
          </Paragraph>
        </div>
      );
    }

    switch (currentStep) {
      case 0:
        return (
          <div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
              <div style={{
                flex: 1,
                padding: 24,
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                backgroundColor: '#fafafa',
              }}>
                <Title level={5}>1. Download Template</Title>
                <Paragraph type="secondary">
                  Excel template matching Ad Ops format
                </Paragraph>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadTemplate}
                  size="large"
                  block
                >
                  Download Template
                </Button>
              </div>

              <div style={{
                flex: 1,
                padding: 24,
                border: file ? '1px solid #52c41a' : '1px solid #d9d9d9',
                borderRadius: 8,
                backgroundColor: file ? '#f6ffed' : '#fafafa',
              }}>
                <Title level={5}>2. Upload File</Title>
                <Paragraph type="secondary">
                  CSV or Excel file with line items
                </Paragraph>
                <Upload
                  accept=".csv,.xlsx,.xls"
                  beforeUpload={handleFileUpload}
                  showUploadList={false}
                  maxCount={1}
                  disabled={!!file}
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    block
                    type={file ? 'default' : 'primary'}
                    disabled={!!file}
                  >
                    Upload File
                  </Button>
                </Upload>
                {file && (
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text type="success">
                      <CheckCircleOutlined /> {file.name}
                    </Text>
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={handleReset}
                      style={{ padding: 0 }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Sheet selector - shown when file has multiple sheets */}
            {parsedData.sheetNames.length > 1 && (
              <div style={{ marginBottom: 24 }}>
                <Alert
                  message="Multiple Sheets Detected"
                  description={
                    <div style={{ marginTop: 8 }}>
                      <Text>Select a sheet to import from:</Text>
                      <Select
                        value={parsedData.selectedSheet}
                        onChange={handleSheetChange}
                        style={{ width: '100%', marginTop: 8 }}
                        size="large"
                      >
                        {parsedData.sheetNames.map(name => (
                          <Select.Option key={name} value={name}>
                            <FileExcelOutlined style={{ marginRight: 8 }} />
                            {name}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  }
                  type="info"
                  showIcon
                  icon={<FileExcelOutlined />}
                />
              </div>
            )}

            <Alert
              message="Supported Columns (Auto-Detected)"
              description={
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <Tag>Line Item Name</Tag>
                  <Tag>Start Date</Tag>
                  <Tag>End Date</Tag>
                  <Tag>DSP Spend</Tag>
                  <Tag>Allocation%</Tag>
                  <Tag>Bid</Tag>
                  <Tag>Segments</Tag>
                  <Tag>Segment ID</Tag>
                  <Tag>Deal Name</Tag>
                  <Tag>ID (Deal)</Tag>
                </div>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </div>
        );

      case 1:
        return (
          <div>
            {/* Header with stats */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              padding: '12px 16px',
              background: '#fafafa',
              borderRadius: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14 }}>✨ AI Smart Mapping</span>
                <Text type="secondary" style={{ fontSize: 12 }}>{file?.name}</Text>
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                <span style={{ color: '#52c41a' }}>{mappingStats.autoMapped} auto-mapped</span>
                <span style={{ color: '#faad14' }}>{mappingStats.reviewSuggested} review suggested</span>
                <span style={{ color: '#8c8c8c' }}>{mappingStats.unmapped} unmapped</span>
              </div>
            </div>

            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
            }}>
              {/* Column headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 40px 1fr 100px',
                padding: '12px 20px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                fontSize: 12,
                color: '#8c8c8c',
              }}>
                <span>📄 Excel Column</span>
                <span></span>
                <span>Line Item Field</span>
                <span style={{ textAlign: 'right' }}>Confidence</span>
              </div>

              {/* Mapping rows */}
              <div style={{ maxHeight: 350, overflow: 'auto' }}>
                {parsedData.headers.map((header) => {
                  const mapping = columnMappings[header];
                  const currentField = mapping?.field;

                  return (
                    <div
                      key={header}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 40px 1fr 100px',
                        padding: '12px 20px',
                        borderBottom: '1px solid #f0f0f0',
                        alignItems: 'center',
                        ...getRowStyle(mapping),
                      }}
                    >
                      <Text strong={!!currentField} style={{ color: currentField ? 'inherit' : '#8c8c8c' }}>
                        {header}
                      </Text>
                      <span style={{ color: '#8c8c8c', textAlign: 'center' }}>→</span>
                      <Select
                        value={currentField}
                        onChange={(value) => handleColumnMappingChange(header, value)}
                        style={getSelectStyle(mapping)}
                        options={fieldOptions}
                        placeholder="Select field..."
                      />
                      <div style={{ textAlign: 'right' }}>
                        {getConfidenceBadge(mapping)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                <InfoCircleOutlined /> Columns set to "-- Skip --" will be ignored. Review yellow items before proceeding.
              </Text>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Title level={5} style={{ marginBottom: 16 }}>
              Preview: {parsedData.rows.length} Line Item(s)
            </Title>

            {validationResult && !validationResult.isValid && (
              <Alert
                message="Validation Errors"
                description={`Found ${validationResult.errors.length} error(s) that must be fixed.`}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {validationResult?.errors?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Text type="danger"><CloseCircleOutlined /> {error}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 120, overflow: 'auto' }}
                />
              </div>
            )}

            {validationResult?.warnings?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.warnings}
                  renderItem={(warning) => (
                    <List.Item>
                      <Text type="warning"><WarningOutlined /> {warning}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 80, overflow: 'auto' }}
                />
              </div>
            )}

            <div style={{
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'auto',
              maxHeight: 250,
            }}>
              <Table
                dataSource={parsedData.rows.map((row, index) => ({
                  key: index,
                  lineNumber: row.lineNumber,
                  ...row.data,
                }))}
                columns={[
                  {
                    title: '#',
                    dataIndex: 'lineNumber',
                    key: 'lineNumber',
                    width: 50,
                    fixed: 'left',
                  },
                  ...parsedData.headers
                    .filter(header => columnMappings[header]?.field)
                    .slice(0, 6) // Limit displayed columns for readability
                    .map(header => ({
                      title: (
                        <Tooltip title={`Excel: "${header}"`}>
                          <span>{LINE_ITEM_FIELDS[columnMappings[header]?.field]?.label || header}</span>
                        </Tooltip>
                      ),
                      dataIndex: header,
                      key: header,
                      ellipsis: true,
                      width: 150,
                    })),
                ]}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
              />
            </div>

            {/* Flight schedule copy checkbox - only show if campaign has flight schedules */}
            {validationResult?.isValid && campaignDefaults.campaignFlightSchedules?.length > 0 && (
              <div style={{ marginTop: 16, marginBottom: 8 }}>
                <Checkbox
                  checked={copyFlightSchedules}
                  onChange={(e) => setCopyFlightSchedules(e.target.checked)}
                >
                  Copy campaign flight schedules ({campaignDefaults.campaignFlightSchedules.length} flight{campaignDefaults.campaignFlightSchedules.length > 1 ? 's' : ''})
                </Checkbox>
                <div style={{ marginLeft: 24, marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Budget will be split equally across flights
                  </Text>
                </div>
              </div>
            )}

            {validationResult?.isValid && (
              <Alert
                message="Ready to Import"
                description={`${parsedData.rows.length} line item(s) will be added to the campaign.`}
                type="success"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            {importing ? (
              <>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                <Title level={4} style={{ marginTop: 24 }}>Importing Line Items...</Title>
                <Progress
                  percent={importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0}
                  status="active"
                  style={{ maxWidth: 400, margin: '16px auto' }}
                />
                <Text type="secondary">
                  Processing {importProgress.current} of {importProgress.total}
                </Text>
              </>
            ) : (
              <>
                <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
                <Title level={4} style={{ marginTop: 24 }}>Import Complete!</Title>
                <Paragraph>Successfully imported {importProgress.total} line item(s).</Paragraph>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getFooterButtons = () => {
    if (importing || isAnalyzing) return null;

    const buttons = [];

    buttons.push(
      <Button key="cancel" onClick={handleClose}>
        {currentStep === 3 ? 'Close' : 'Cancel'}
      </Button>
    );

    switch (currentStep) {
      case 0:
        // Only show Next button if we have parsed data and it's a single sheet
        // (multi-sheet files auto-advance after sheet selection)
        if (file && parsedData.headers.length > 0 && parsedData.sheetNames.length <= 1) {
          buttons.push(
            <Button
              key="next"
              type="primary"
              onClick={() => setCurrentStep(1)}
            >
              Next: Map Columns
            </Button>
          );
        }
        break;

      case 1:
        buttons.push(
          <Button key="back" onClick={() => setCurrentStep(0)}>
            Back
          </Button>
        );
        buttons.push(
          <Button
            key="preview"
            type="primary"
            onClick={handleProceedToPreview}
            disabled={!Object.values(columnMappings).some(v => v?.field)}
          >
            Next: Preview
          </Button>
        );
        break;

      case 2:
        buttons.push(
          <Button key="back" onClick={() => setCurrentStep(1)}>
            Back
          </Button>
        );
        buttons.push(
          <Button
            key="import"
            type="primary"
            onClick={handleImport}
            disabled={!validationResult?.isValid}
          >
            Import {parsedData.rows.length} Line Item(s)
          </Button>
        );
        break;

      case 3:
        if (!importing) {
          buttons.push(
            <Button key="importMore" type="primary" onClick={handleReset}>
              Import More
            </Button>
          );
        }
        break;

      default:
        break;
    }

    return buttons;
  };

  return (
    <Modal
      title={
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          Import Line Items
        </span>
      }
      width={800}
      open={visible}
      onCancel={importing || isAnalyzing ? null : handleClose}
      closable={!importing && !isAnalyzing}
      maskClosable={!importing && !isAnalyzing}
      footer={getFooterButtons()}
    >
      <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
        <Step title="Upload" />
        <Step title="Map Columns" />
        <Step title="Preview" />
        <Step title="Import" />
      </Steps>

      {renderStepContent()}
    </Modal>
  );
};

export default LineItemImportModal;
